import React, {JSX, ReactNode} from "react";
import classNames from "classnames";
import {extractViewTypeClassesFromProps} from '@ok/utils/ViewTypesUtility';

import classes from "./Title.styl";
import {IAttrs} from "~/utils/types/IAttrs";

export const titleLevels = ['h1', 'h2', 'h3'] as const;
const DEFAULT_TITLE_LEVEL = "h2";

export interface ITitleProps extends ITitlePropsChildren {
    link?: string;
    asideLinks?: TitleLink[];
    afterText?: ReactNode;
    delimiter?: boolean;
    titleLevel?: typeof titleLevels[number];
    viewTypes?: ViewType | ViewType[];
    modifier?: string;
    secondaryText?: string;
    className?: string;
    textClassName?: string;
    beforeTitleText?: () => JSX.Element;
    afterTitleText?: () => JSX.Element;
    asideComponent?: () => JSX.Element;
    beforeTitleComponent?: JSX.Element | null;
    // fallback to normal font-weight for backward compatibility
    isClassicRDKView?: boolean;
    /** Применять только стили от соответсвующего модификатора titleLevel, игнорируюя тег */
    ignoreTitleLevelTag?: boolean;
}

interface ITitlePropsChildren {
    children: React.ReactText
}

export const Title: React.FC<ITitleProps> = ({
    link,
    asideLinks,
    delimiter = true,
    titleLevel = DEFAULT_TITLE_LEVEL,
    viewTypes,
    modifier,
    secondaryText,
    className,
    textClassName,
    beforeTitleText,
    afterTitleText,
    asideComponent,
    isClassicRDKView = true,
    children,
    beforeTitleComponent,
    ignoreTitleLevelTag,
}) => {

    const titleClasses = {
        [classes["title"]]: true,
        [classes["__delimiter"]]: delimiter,
        [classes[`__${titleLevel}`]]: true
    };

    const isClassicRDKViewClass = {
        [classes["is_classic-view"]]: isClassicRDKView
    };

    let viewTypesClasses = extractViewTypeClassesFromProps({viewTypes})?.map(className => classes[className as ViewType]);

    const HeadingTag = ignoreTitleLevelTag ? DEFAULT_TITLE_LEVEL : titleLevel;

    return (
        <div className={classNames(titleClasses, viewTypesClasses, modifier, className)}>
            {beforeTitleText &&
            <div className={classes["title_before_text"]}>
                {beforeTitleText()}
            </div>}
            {!link && beforeTitleComponent}
            <div className={classes["title_text"]}>
                {link ?
                    <a className={classes["title_link"]}
                       href={link}
                    >
                        {beforeTitleComponent}
                        <HeadingTag className={classNames(classes["title_text_main"], textClassName, isClassicRDKViewClass)}>
                            {children}
                        </HeadingTag>
                    </a>
                    :
                    <HeadingTag className={classNames(classes["title_text_main"], textClassName, isClassicRDKViewClass)}>
                        {children}
                    </HeadingTag>}
                {secondaryText &&
                <div className={classNames(classes["title_text_secondary"])}>
                    {secondaryText}
                </div>}
            </div>

            {afterTitleText &&
                <div className={classes["title_after_text"]}>
                    {afterTitleText()}
                </div>}
            {(asideLinks || asideComponent) ?
                <div className={classes["title_aside"]}>
                    {asideLinks &&
                    <div className={classes["title_aside_links"]}>
                        {renderLinks(asideLinks)}
                    </div>}
                    {asideComponent &&
                    <div className={classes["title_aside_component"]}>
                        {asideComponent()}
                    </div>}
                </div>
            : null}
        </div>
    );
};

function renderLinks(links: TitleLink[]) {
    return links.map(({label, url, attrs}, index) => {
        if (url && label) {
            return (
                <a href={url} className={classes["title_aside-link"]} {...attrs} key={index}>
                    {label}
                </a>
            );
        }

        return null;
    });
}

export type TitleLink = { label: string, url: string, attrs?: IAttrs };

export const enum ViewType {
    SHOUT = '__shout', // более крупный текст
    PROMO = '__promo', // жирный текст по центру с увеличенным отступом в бсолютном значении
    PROMOTED = '__promoted', // даем значительный отступ сверху (как правило - для полностраничных промо-страничек)
}
